import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";
import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import { certificationPaths } from "@/domains/certifications/routes";
import { WeekProgressPanelProps } from "@/features/certifications/module/components/types";
import {
  areOnlyVideosOutstanding,
  buildRequirementRows,
} from "@/features/certifications/module/utils";
import { MODULE_STATUSES } from "@/domains/certifications/constants";
import { REQUIREMENT_ROW_KEYS } from "@/features/certifications/module/constants";

import "./styles.css";

const BAR_STROKE_WIDTH = 4;
const CHECK_ICON_SIZE = 14;

const WeekProgressPanel = ({
  slug,
  week,
  nextWeek,
  pathTitle,
  editNeeded,
}: WeekProgressPanelProps) => {
  const { t } = useTranslation();
  const rows = buildRequirementRows(t, week, editNeeded);
  const isNextUnlocked = Boolean(
    nextWeek && nextWeek.status !== MODULE_STATUSES.LOCKED,
  );

  let lockedNote = t("certification.module.rail.completeToUnlock");
  if (week.status === MODULE_STATUSES.COMPLETE) {
    lockedNote = t("certification.module.rail.unlockingSoon", {
      position: nextWeek?.position,
    });
  } else if (areOnlyVideosOutstanding(week)) {
    lockedNote = t("certification.module.rail.watchToUnlock");
  }

  return (
    <section className="certification-card flex flex-col gap-300">
      <header className="module-rail-header flex flex-col gap-50">
        <h2 className="certification-heading body-m-medium">
          {t("certification.module.rail.title", { position: week.position })}
        </h2>

        <span className="certification-muted body-xs-regular">{pathTitle}</span>
      </header>

      <div className="module-rail-body flex flex-col gap-300">
        {rows.map((row) => (
          <BaseProgressBar
            key={row.key}
            value={row.percent}
            total={100}
            strokeWidth={BAR_STROKE_WIDTH}
            strokeColor="var(--primary-600)"
            status="normal"
            className="module-progress-bar full-width"
            label={<span className="body-s-medium">{row.label}</span>}
            helpText={
              row.isComplete ? (
                <span className="module-rail-complete flex items-center gap-50 body-xs-regular">
                  <Check size={CHECK_ICON_SIZE} className="flex-none" />

                  {row.value}
                </span>
              ) : (
                <span
                  className={`body-xs-regular ${row.isWarning ? "module-warning-text" : "certification-muted"}`}
                >
                  {row.value}
                </span>
              )
            }
          />
        ))}

        {rows.length === 1 && rows[0]?.key === REQUIREMENT_ROW_KEYS.VIDEOS && (
          <p className="certification-muted body-xs-regular">
            {t("certification.module.rail.videosOnly")}
          </p>
        )}
      </div>

      <footer
        className={`module-rail-footer flex flex-col gap-200 ${isNextUnlocked || !nextWeek ? "module-rail-footer-open" : ""}`}
      >
        {nextWeek ? (
          <>
            <BaseButton
              type={isNextUnlocked ? BUTTON_TYPES.LINK : BUTTON_TYPES.BUTTON}
              to={certificationPaths.module(slug, nextWeek.module.id)}
              disabled={!isNextUnlocked}
              isFullWidth
            >
              {t("certification.module.rail.continue", {
                position: nextWeek.position,
              })}
            </BaseButton>

            <p
              className={`module-rail-note body-xs-regular ${isNextUnlocked ? "module-rail-note-open" : ""}`}
            >
              {isNextUnlocked
                ? t("certification.module.rail.unlocked", {
                    position: nextWeek.position,
                  })
                : lockedNote}
            </p>
          </>
        ) : (
          <BaseButton
            type={BUTTON_TYPES.LINK}
            to={certificationPaths.overview(slug)}
            isFullWidth
          >
            {t("certification.module.rail.backToOverview")}
          </BaseButton>
        )}
      </footer>
    </section>
  );
};

export default WeekProgressPanel;
